import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";

export class ApiError extends Error {
  status: number;
  code?: string;

  constructor(status: number, message: string, code?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

interface ApiErrorPayload {
  error?: string;
  message?: string;
}

export type NativeSessionScope = "customer" | "staff" | "owner";

const configuredApiOrigin = import.meta.env.VITE_API_ORIGIN?.trim().replace(/\/+$/u, "");
const defaultNativeApiOrigin = "https://chaykhana-izumrud.ru";
const publicOrigin = configuredApiOrigin || (Capacitor.isNativePlatform() ? defaultNativeApiOrigin : "");
const nativeSessionStorageKeyPrefix = "izumrud_native_session_token";

function isNativeSessionStorageAvailable() {
  return typeof window !== "undefined" && Capacitor.isNativePlatform();
}

function getNativeSessionStorageKey(scope: NativeSessionScope) {
  return `${nativeSessionStorageKeyPrefix}:${scope}`;
}

function resolveNativeSessionScope(path: string): NativeSessionScope | null {
  if (path.startsWith("/api/staff/")) {
    return "staff";
  }

  if (path.startsWith("/api/owner/")) {
    return "owner";
  }

  if (
    path.startsWith("/api/auth/") ||
    path.startsWith("/api/customer/") ||
    path === "/api/orders" ||
    path.startsWith("/api/orders/") ||
    path.startsWith("/api/payments/robokassa/")
  ) {
    return "customer";
  }

  return null;
}

export function getNativeSessionToken(scope: NativeSessionScope) {
  if (!isNativeSessionStorageAvailable()) {
    return "";
  }

  try {
    return window.localStorage.getItem(getNativeSessionStorageKey(scope))?.trim() ?? "";
  } catch {
    return "";
  }
}

export function setNativeSessionToken(scope: NativeSessionScope, token: string) {
  if (!isNativeSessionStorageAvailable()) {
    return;
  }

  const normalizedToken = token.trim();

  try {
    if (normalizedToken) {
      window.localStorage.setItem(getNativeSessionStorageKey(scope), normalizedToken);
      return;
    }

    window.localStorage.removeItem(getNativeSessionStorageKey(scope));
  } catch {
    // Ignore storage failures so the API layer can keep working with cookies on web.
  }
}

export function clearNativeSessionToken(scope: NativeSessionScope) {
  if (!isNativeSessionStorageAvailable()) {
    return;
  }

  try {
    window.localStorage.removeItem(getNativeSessionStorageKey(scope));
  } catch {
    // Ignore storage failures so logout still completes server-side.
  }
}

export function buildPublicUrl(path: string) {
  if (/^https?:\/\//iu.test(path)) {
    return path;
  }

  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return publicOrigin ? `${publicOrigin}${normalizedPath}` : normalizedPath;
}

export function buildRequestUrl(path: string) {
  return buildPublicUrl(path);
}

function buildRequestHeaders(path: string, init?: RequestInit) {
  const requestUrl = buildRequestUrl(path);
  const headers = new Headers(init?.headers);
  const nativeSessionScope = resolveNativeSessionScope(path);
  const nativeSessionToken = nativeSessionScope ? getNativeSessionToken(nativeSessionScope) : "";

  if (init?.body != null && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  if (nativeSessionToken && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${nativeSessionToken}`);
  }

  return {
    requestUrl,
    headers
  };
}

async function performRequest(path: string, init?: RequestInit) {
  const { requestUrl, headers } = buildRequestHeaders(path, init);

  try {
    return await fetch(requestUrl, {
      credentials: "include",
      ...init,
      headers
    });
  } catch {
    throw new ApiError(
      0,
      "РќРµ СѓРґР°Р»РѕСЃСЊ СЃРІСЏР·Р°С‚СЊСЃСЏ СЃ СЃРµСЂРІРµСЂРѕРј. РџСЂРѕРІРµСЂСЊС‚Рµ СЃРѕРµРґРёРЅРµРЅРёРµ Рё РїРѕРІС‚РѕСЂРёС‚Рµ РїРѕРїС‹С‚РєСѓ.",
      "NETWORK_ERROR"
    );
  }
}

function buildApiError(status: number, payload?: ApiErrorPayload) {
  return new ApiError(
    status,
    payload?.message ?? "РЎРµСЂРІРµСЂ РІРµСЂРЅСѓР» РѕС€РёР±РєСѓ.",
    payload?.error
  );
}

async function parseApiErrorResponse(response: Response) {
  const text = await response.text();

  if (!text) {
    throw buildApiError(response.status);
  }

  try {
    throw buildApiError(response.status, JSON.parse(text) as ApiErrorPayload);
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    throw buildApiError(response.status);
  }
}

export async function apiRequestText(path: string, init?: RequestInit): Promise<string> {
  const response = await performRequest(path, init);

  if (!response.ok) {
    await parseApiErrorResponse(response);
  }

  return response.text();
}

export async function openApiHtmlDocument(path: string, init?: RequestInit) {
  if (typeof document === "undefined") {
    throw new ApiError(0, "HTML checkout РґРѕСЃС‚СѓРїРµРЅ С‚РѕР»СЊРєРѕ РІ Р±СЂР°СѓР·РµСЂРµ.", "BROWSER_ONLY");
  }

  const requestUrl = buildRequestUrl(path);

  if (Capacitor.isNativePlatform()) {
    await Browser.open({ url: requestUrl });
    return;
  }

  void init;
  window.location.assign(requestUrl);
}

export async function apiRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await performRequest(path, init);

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  let data: T | ApiErrorPayload = {} as T;

  if (text) {
    try {
      data = JSON.parse(text) as T | ApiErrorPayload;
    } catch {
      throw new ApiError(
        response.status || 500,
        "РЎРµСЂРІРёСЃ РІРµСЂРЅСѓР» РЅРµРѕР¶РёРґР°РЅРЅС‹Р№ РѕС‚РІРµС‚ РІРјРµСЃС‚Рѕ JSON. РџСЂРѕРІРµСЂСЊС‚Рµ, С‡С‚Рѕ РјРѕР±РёР»СЊРЅРѕРµ РїСЂРёР»РѕР¶РµРЅРёРµ РїРѕРґРєР»СЋС‡РµРЅРѕ Рє СЂР°Р±РѕС‡РµРјСѓ API.",
        "INVALID_JSON_RESPONSE"
      );
    }
  }

  if (!response.ok) {
    throw buildApiError(response.status, data as ApiErrorPayload);
  }

  return data as T;
}
